import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { socketService } from '../services/socketService';

interface GameState {
  phase: string;
  leaderName: string;
  players: string[];
  joinedPlayers: string[];
  readyPlayers: string[];
  setupComplete: boolean;
  error?: string;
  deckColors?: string[];
}

const WaitingRoom: React.FC = () => {
  const navigate = useNavigate();
  const { gameRole, setGamePhase } = useGameStore();
  const [selectedName, setSelectedName] = useState<string>('');
  const [isReady, setIsReady] = useState(false);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const socket = socketService.connect();

    const handleGameState = (state: GameState) => {
      console.log('Received game state:', state);
      setGameState(state);
      
      // Update ready status if this player is ready
      if (selectedName) {
        if (state.readyPlayers.includes(selectedName)) {
          setIsReady(true);
        }
      }

      if (state.phase === 'playing') {
        setGamePhase('playing');
        navigate('/game');
      }
    };

    // Get initial game state
    socketService.checkGameStatus()
      .then((response: GameState) => {
        console.log('Initial game state:', response);
        if (response.error) {
          setError(response.error);
          return;
        }
        setGameState(response);
      })
      .catch((err: Error) => {
        console.error('Failed to get game state:', err);
        setError('Failed to get game state');
      });

    socket.on('gameState', handleGameState);
    socket.on('gameStarted', (data: any) => {
      console.log('Game started:', data);
      setGamePhase('playing');
      // Small delay to ensure server has fully set up the game state
      setTimeout(() => {
        navigate('/game');
      }, 500);
    });
    socket.on('error', (error: { message: string }) => {
      console.error('Socket error:', error);
      setError(error.message);
      setIsProcessing(false);
    });

    return () => {
      socket.off('gameState', handleGameState);
      socket.off('gameStarted');
      socket.off('error');
    };
  }, [navigate, selectedName, setGamePhase]);

  const handlePlayerSelect = async (name: string) => {
    if (gameRole === 'leader') return;
    if (isProcessing) return;
    if (name === gameState?.leaderName) return;
    if (gameState?.readyPlayers.includes(name)) return;
    
    try {
      setIsProcessing(true);
      setError('');
      console.log('Selecting player:', name);
      
      const socket = socketService.connect();
      await new Promise<void>((resolve, reject) => {
        socket.emit('setPlayerName', { name }, (response: any) => {
          console.log('setPlayerName response:', response);
          if (response?.error) {
            reject(new Error(response.error));
          } else {
            resolve();
          }
        });
      });

      console.log('Player selected successfully:', name);
      setSelectedName(name);
      setIsReady(false);
    } catch (err) {
      console.error('Error selecting player:', err);
      setError(err instanceof Error ? err.message : 'Failed to select player');
      setSelectedName('');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReady = async () => {
    if (!selectedName || isProcessing) return;

    try {
      setIsProcessing(true);
      setError('');
      
      console.log('Setting ready status for:', selectedName);
      const socket = socketService.connect();
      
      await new Promise<void>((resolve, reject) => {
        socket.emit('playerReady', { name: selectedName }, (response: any) => {
          console.log('playerReady response:', response);
          if (response?.error) {
            reject(new Error(response.error));
          } else {
            resolve();
          }
        });
      });
      
      console.log('Player ready status set successfully');
      setIsReady(true);
    } catch (err) {
      console.error('Error setting ready status:', err);
      setError(err instanceof Error ? err.message : 'Failed to set ready status');
      setIsReady(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStartGame = async () => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      setError('');
      
      console.log('Starting game...');
      const socket = socketService.connect();
      
      await new Promise<void>((resolve, reject) => {
        socket.emit('startGame', {}, (response: any) => {
          console.log('startGame response:', response);
          if (response?.error) {
            reject(new Error(response.error));
          } else {
            resolve();
          }
        });
      });
      
      console.log('Game started successfully');
    } catch (err) {
      console.error('Error starting game:', err);
      setError(err instanceof Error ? err.message : 'Failed to start game');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderPlayerActions = () => {
    console.log('Rendering player actions:', {
      gameRole,
      selectedName,
      isReady,
      readyPlayers: gameState?.readyPlayers
    });

    if (!gameState) return null;

    // Leader actions
    if (gameRole === 'leader') {
      const nonLeaderPlayers = gameState.players.filter(name => name !== gameState.leaderName);
      const allPlayersReady = nonLeaderPlayers.every(name => gameState.readyPlayers.includes(name));
      
      console.log('Leader check:', {
        nonLeaderPlayers,
        readyPlayers: gameState.readyPlayers,
        allPlayersReady
      });

      return allPlayersReady && nonLeaderPlayers.length > 0 ? (
        <ActionButton 
          onClick={handleStartGame}
          disabled={isProcessing}
          $isStart
        >
          {isProcessing ? 'Starting...' : 'Start Game'}
        </ActionButton>
      ) : (
        <WaitingMessage>
          Waiting for all players to be ready... ({gameState.readyPlayers.length}/{nonLeaderPlayers.length})
        </WaitingMessage>
      );
    }

    // Member actions
    if (!selectedName) {
      return <WaitingMessage>Please select your name to join the game</WaitingMessage>;
    }

    if (gameState.readyPlayers.includes(selectedName)) {
      return <WaitingMessage>Waiting for other players and leader to start the game...</WaitingMessage>;
    }

    return (
      <ActionButton 
        onClick={handleReady}
        disabled={isProcessing}
        $isStart={false}
      >
        {isProcessing ? 'Processing...' : 'Ready'}
      </ActionButton>
    );
  };

  const getDeckImages = () => {
    if (!gameState) return [];
    const playerCount = gameState.players.length;
    if (playerCount <= 1) return [];
    
    console.log('WaitingRoom - gameState.deckColors:', gameState.deckColors);
    console.log('WaitingRoom - playerCount:', playerCount);
    
    // Use server-provided deck colors if available
    if (gameState.deckColors && gameState.deckColors.length > 0) {
      console.log('Using server-provided deck colors:', gameState.deckColors);
      return gameState.deckColors.map(color => 
        color === 'blue' ? '/images/cards/rhombus_blue.png' : '/images/cards/rhombus_red.png'
      );
    }
    
    // For 2 players, show one deck until server provides the actual color
    if (playerCount === 2) {
      console.log('No server deck colors, showing one deck for 2 players');
      return ['/images/cards/rhombus_blue.png']; // Default to blue for 2 players
    } else {
      console.log('No server deck colors, showing both for 3+ players');
      return ['/images/cards/rhombus_blue.png', '/images/cards/rhombus_red.png'];
    }
  };

  const deckImages = getDeckImages();

  if (!gameState) {
    return <LoadingContainer>Loading...</LoadingContainer>;
  }

  return (
    <Container>
      <ContentWrapper>
        <Card>
          <Title>Waiting Room</Title>
          <GameInfo>
            <InfoItem>
              <InfoLabel>Leader:</InfoLabel>
              <InfoValue>{gameState.leaderName}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Players Ready:</InfoLabel>
              <InfoValue>{gameState.readyPlayers.length} / {gameState.players.length - 1}</InfoValue>
            </InfoItem>
          </GameInfo>

          <Section>
            <SectionTitle>Players:</SectionTitle>
            <PlayerList>
              {gameState.players.map(name => {
                const isSelected = name === selectedName;
                const isReady = name === gameState.leaderName || gameState.readyPlayers.includes(name);
                const isJoined = gameState.joinedPlayers.includes(name);
                const isSelectable = gameRole === 'member' && 
                                   name !== gameState.leaderName &&
                                   !gameState.readyPlayers.includes(name);
                
                return (
                  <StyledPlayerItem 
                    key={name}
                    $isLeader={name === gameState.leaderName}
                    $isSelected={isSelected}
                    $isJoined={isJoined}
                    $isReady={isReady}
                    onClick={() => isSelectable && !isProcessing && handlePlayerSelect(name)}
                    style={{ 
                      cursor: isSelectable && !isProcessing ? 'pointer' : 'default',
                      opacity: isProcessing ? 0.7 : 1
                    }}
                  >
                    <PlayerName>
                      {name} {name === gameState.leaderName && '👑'}
                    </PlayerName>
                    <StyledPlayerStatus $isReady={isReady}>
                      {name === gameState.leaderName ? 'Leader' : 
                       isReady ? 'Ready' : 
                       isJoined ? 'Joined' : 'Waiting...'}
                    </StyledPlayerStatus>
                  </StyledPlayerItem>
                );
              })}
            </PlayerList>
          </Section>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {renderPlayerActions()}
        </Card>
        
        {deckImages.length > 0 && (
          <DeckPreview>
            <DeckContainer>
              {deckImages.map((src, deckIndex) => (
                <SingleDeckStack key={deckIndex}>
                  {[...Array(5)].map((_, stackIndex) => (
                    <DeckImage 
                      key={`${deckIndex}-${stackIndex}`}
                      src={src}
                      alt={`Deck ${deckIndex + 1} Card ${stackIndex + 1}`}
                      $stackIndex={stackIndex}
                    />
                  ))}
                </SingleDeckStack>
              ))}
            </DeckContainer>
            <DeckInfo>
              {gameState.players.length === 2 
                ? '1 Deck Game'
                : '2 Deck Game'}
            </DeckInfo>
          </DeckPreview>
        )}
      </ContentWrapper>
    </Container>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 1.2em;
  color: white;
  background: radial-gradient(circle at center, #2a8d2a 0%, #1a5d1a 50%, #0f3d0f 100%);
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: radial-gradient(circle at center, #2a8d2a 0%, #1a5d1a 50%, #0f3d0f 100%);
  padding: 20px;
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  max-width: 1200px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  flex: 1;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const Title = styled.h1`
  text-align: center;
  color: white;
  font-size: 28px;
  margin-bottom: 30px;
  font-weight: 600;
`;

const GameInfo = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
`;

const InfoLabel = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
`;

const InfoValue = styled.span`
  color: white;
  font-size: 18px;
  font-weight: 600;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 15px;
  font-size: 18px;
  font-weight: 500;
`;

const PlayerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledPlayerItem = styled.div<{
  $isLeader: boolean;
  $isSelected: boolean;
  $isJoined: boolean;
  $isReady: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background: ${props => {
    if (props.$isLeader) return 'linear-gradient(135deg, #9b59b6, #8e44ad)';
    if (props.$isSelected) return 'linear-gradient(135deg, #3498db, #2980b9)';
    if (props.$isReady) return 'rgba(39, 174, 96, 0.2)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  border-radius: 12px;
  transition: all 0.3s ease;
  border: ${props => props.$isSelected ? '2px solid rgba(52, 152, 219, 0.5)' : '2px solid transparent'};

  &:hover {
    transform: ${props => (!props.$isLeader && !props.$isReady) ? 'translateY(-2px)' : 'none'};
    box-shadow: ${props => (!props.$isLeader && !props.$isReady) ? '0 4px 15px rgba(0, 0, 0, 0.2)' : 'none'};
  }
`;

const PlayerName = styled.span`
  flex: 1;
  margin-right: 10px;
  color: white;
  font-size: 16px;
  font-weight: 500;
`;

const StyledPlayerStatus = styled.span<{ $isReady: boolean }>`
  padding: 6px 12px;
  border-radius: 20px;
  background: ${props => 
    props.$isReady ? 'rgba(39, 174, 96, 0.3)' : 'rgba(255, 152, 0, 0.3)'
  };
  color: ${props => props.$isReady ? '#2ecc71' : '#f39c12'};
  font-size: 14px;
  font-weight: 500;
`;

const ActionButton = styled.button<{ $isStart: boolean }>`
  display: block;
  width: 100%;
  max-width: 300px;
  margin: 20px auto;
  padding: 15px 24px;
  background: ${props => props.$isStart
    ? 'linear-gradient(135deg, #27ae60, #219a52)'
    : 'linear-gradient(135deg, #3498db, #2980b9)'};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px ${props => props.$isStart
    ? 'rgba(39, 174, 96, 0.3)'
    : 'rgba(52, 152, 219, 0.3)'};

  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
    opacity: 0.7;
    box-shadow: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${props => props.$isStart
      ? 'rgba(39, 174, 96, 0.4)'
      : 'rgba(52, 152, 219, 0.4)'};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const WaitingMessage = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  margin: 20px 0;
  font-size: 16px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  text-align: center;
  margin: 20px 0;
  padding: 12px;
  border-radius: 10px;
  background: rgba(231, 76, 60, 0.1);
`;

const DeckPreview = styled.div`
  flex: 0 0 350px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
`;

const DeckContainer = styled.div`
  display: flex;
  gap: 40px;
  align-items: center;
  justify-content: center;
`;

const SingleDeckStack = styled.div`
  position: relative;
  width: 146px;
  height: 198px;
`;

const DeckImage = styled.img<{ $stackIndex?: number }>`
  position: absolute;
  width: 146px;
  height: 198px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  transform: ${props => {
    const offset = (props.$stackIndex || 0) * 2;
    return `translate(${offset}px, -${offset}px)`;
  }};
  z-index: ${props => 10 - (props.$stackIndex || 0)};
`;

const DeckInfo = styled.div`
  color: white;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  padding: 15px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  width: 100%;
`;

export default WaitingRoom; 